import { Fragment, useMemo, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, DataGrid, KeenIcon } from '@/components';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { usePermissions } from '@/hooks/usePermissions';
import usePagination from '@/hooks/usePagination';
import { useListEntitlementGrantsQuery } from '@/services/EntitlementGrantService';
import {
  EntitlementActorType,
  EntitlementGrant,
  EntitlementGrantStatus,
  EntitlementGrantType,
} from '@/types/entitlement-grant.types';

const STATUS_BADGE: Record<EntitlementGrantStatus, string> = {
  [EntitlementGrantStatus.ACTIVE]: 'badge badge-success',
  [EntitlementGrantStatus.EXPIRED]: 'badge badge-warning',
  [EntitlementGrantStatus.CANCELLED]: 'badge badge-danger',
};

const formatWindow = (start: string, end: string) => {
  const fmt = (d: string) =>
    new Date(d).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  return `${fmt(start)} – ${fmt(end)}`;
};

const stripHtml = (html: string | null): string => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').trim();
};

const formatSource = (source: string | null): string => {
  if (!source) return '-';
  if (source.startsWith('admin_manual:')) return 'Admin';
  if (source.startsWith('trial_overlay:')) return 'Trial';
  return source;
};

const EntitlementGrantManagement = () => {
  const { currentLayout } = useLayout();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const canRead = hasPermission('entitlement_grant_management_read');
  const canCreate = hasPermission('entitlement_grant_management_create');

  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    pagination,
    setPagination,
    debouncedValue: search,
    handleSearch,
    searchTerm,
  } = usePagination();

  const [actorType, setActorType] = useState<EntitlementActorType | ''>('');
  const [grantType, setGrantType] = useState<EntitlementGrantType | ''>('');
  const [status, setStatus] = useState<EntitlementGrantStatus | ''>('');

  const { data, isLoading } = useListEntitlementGrantsQuery(
    {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: search || undefined,
      actor_type: actorType || undefined,
      grant_type: grantType || undefined,
      status: status || undefined,
    },
    { skip: !canRead }
  );

  useEffect(() => {
    if (data) {
      handleTotalItems(data.total);
      handleTotalPages(Math.ceil(data.total / pagination.pageSize) || 1);
    }
  }, [data, handleTotalItems, handleTotalPages, pagination.pageSize]);

  const rows = useMemo(() => data?.data ?? [], [data]);

  const columns = useMemo(
    () => [
      {
        id: 'actor',
        header: 'Actor',
        cell: ({ row }: { row: { original: EntitlementGrant } }) => (
          <div className="flex flex-col gap-0.5">
            {row.original.actor_type}
            <span className="text-sm font-medium">
              {row.original.actor_name || row.original.actor_id}
            </span>
          </div>
        ),
      },
      {
        id: 'grant_type',
        header: 'Type',
        cell: ({ row }: { row: { original: EntitlementGrant } }) => (
          <span>{row.original.grant_type}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }: { row: { original: EntitlementGrant } }) => (
          <span className={STATUS_BADGE[row.original.status] ?? 'badge'}>
            {row.original.status}
          </span>
        ),
      },
      {
        id: 'window',
        header: 'Window',
        cell: ({ row }: { row: { original: EntitlementGrant } }) => (
          <span className="text-xs text-gray-600">
            {formatWindow(row.original.start_at, row.original.end_at)}
          </span>
        ),
      },
      {
        id: 'subscription',
        header: 'Subscription',
        cell: ({ row }: { row: { original: EntitlementGrant } }) => (
          <span className="text-sm">
            {stripHtml(row.original.subscription_name) ||
              row.original.reference_subscription_id ||
              '-'}
          </span>
        ),
      },
      {
        id: 'source',
        header: 'Source',
        cell: ({ row }: { row: { original: EntitlementGrant } }) => (
          <span className="text-sm text-gray-600">
            {formatSource(row.original.source)}
          </span>
        ),
      },
      {
        id: 'created_by',
        header: 'Created By',
        cell: ({ row }: { row: { original: EntitlementGrant } }) => (
          <span className="text-sm">
            {row.original.created_by_name || row.original.created_by || '-'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }: { row: { original: EntitlementGrant } }) => (
          <Link
            className="btn btn-sm btn-light"
            to={`/configuration/entitlement-grants/${row.original.id}`}
          >
            View
          </Link>
        ),
      },
    ],
    []
  );

  if (!canRead) {
    return (
      <Container>
        <p>You do not have permission to view entitlement grants.</p>
      </Container>
    );
  }

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle id="entitlement-grant-management-heading" />
              <ToolbarDescription>
                Manage capability elevation grants for actors.
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              {canCreate && (
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() =>
                    navigate('/configuration/entitlement-grants/create')
                  }
                >
                  Create Grant
                </button>
              )}
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}

      <Container>
        <div className="grid gap-3 md:grid-cols-4 mb-5">
          <label className="input input-sm flex items-center gap-2">
              <KeenIcon icon="magnifier" className="text-gray-400 text-sm" />
              <input
                type="text"
                placeholder="Search actor or source"
                value={searchTerm}
                onChange={handleSearch}
                className="grow bg-transparent outline-none text-sm"
              />
            </label>
            <select
              className="select select-bordered"
              value={actorType}
              onChange={(e) => {
                setActorType(e.target.value as EntitlementActorType | '');
                setPagination((p) => ({ ...p, pageIndex: 0 }));
              }}
            >
              <option value="">All Actor Types</option>
              {Object.values(EntitlementActorType).map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
            <select
              className="select select-bordered"
              value={grantType}
              onChange={(e) => {
                setGrantType(e.target.value as EntitlementGrantType | '');
                setPagination((p) => ({ ...p, pageIndex: 0 }));
              }}
            >
              <option value="">All Grant Types</option>
              {Object.values(EntitlementGrantType).map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
            <select
              className="select select-bordered"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as EntitlementGrantStatus | '');
                setPagination((p) => ({ ...p, pageIndex: 0 }));
              }}
            >
              <option value="">All Statuses</option>
              {Object.values(EntitlementGrantStatus).map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
        </div>

        <DataGrid
          columns={columns}
          data={rows}
          loading={isLoading}
          serverSide={true}
          pagination={{
            page: pagination.pageIndex,
            size: pagination.pageSize,
            totalItems,
            totalPages,
            more: pagination.pageIndex + 1 < totalPages,
            setPagination,
          }}
        />
      </Container>
    </Fragment>
  );
};

export { EntitlementGrantManagement };
